import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { Visibility, VisibilityOff } from '@mui/icons-material';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import { Alert, Avatar, Button, Container, IconButton, InputAdornment, Stack, TextField, Typography } from '@mui/material';
import { Auth } from 'aws-amplify';
import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { NavSections } from '../navigation/NavSections';
import { isValidPassword, isValidUsername, passwordReminder } from './common';
const debug = process.env.REACT_APP_DEBUG && false;
var ResetStages;
(function (ResetStages) {
    ResetStages[ResetStages["ASK_USER_TO_REQUEST_CODE"] = 0] = "ASK_USER_TO_REQUEST_CODE";
    ResetStages[ResetStages["ASK_USER_TO_ENTER_RECEIVED_CODE"] = 1] = "ASK_USER_TO_ENTER_RECEIVED_CODE";
})(ResetStages || (ResetStages = {}));
// Send confirmation code to user's email
const requestConfirmationCode = async (username, setMessage, setError, setEmail, setResetStage) => {
    try {
        const res = await Auth.forgotPassword(username);
        if (res) {
            const successMsg = res;
            if (successMsg.CodeDeliveryDetails?.Destination) {
                setMessage(`An access code has been sent to your email address ${successMsg.CodeDeliveryDetails?.Destination}`);
                setEmail(successMsg.CodeDeliveryDetails?.Destination);
                setResetStage(ResetStages.ASK_USER_TO_ENTER_RECEIVED_CODE);
            }
            else {
                setMessage(JSON.stringify(res));
            }
        }
    }
    catch (e) {
        console.log('Err in requestConfirmationCode', e.message);
        const errMsgMap = {
            default: `Sorry, that's not a username which matches the pattern used in this system. Please try again`,
            'Username/client id combination not found': `We are having trouble finding ${username}, please check that you have typed it correctly`,
            'Attempt limit exceeded, please try after some time': 'You have exceeded the number of reset attempts, please try again later.',
        };
        const [_, matchedError] = Object.entries(errMsgMap).find(([errMsg, ourMsg]) => (e.message || '').includes(errMsg)) ?? [
            'none',
            errMsgMap.default,
        ];
        setError(matchedError);
    }
};
// Collect confirmation code and new password, then
const submitNewPassword = async (username, code, new_password, setMessage, setError, setResetStage, navigate) => {
    try {
        const res = await Auth.forgotPasswordSubmit(username, code, new_password);
        debug && console.log('requestConfirmationCode res', res);
        if (res === 'SUCCESS') {
            setMessage(`Your password has been changed`);
            setTimeout(() => navigate(`/${NavSections.LOGIN}`), 1500);
        }
        else {
            setError('Something unusual has happened');
            setResetStage(ResetStages.ASK_USER_TO_REQUEST_CODE);
        }
    }
    catch (e) {
        console.log('Err in requestConfirmationCode', e.message);
        const errMsgMap = {
            default: `Sorry, that's not a password which matches can used in this system. Please try again`,
            'Invalid verification code provided, please try again.': 'Invalid verification code provided, please try again.',
            'Password does not conform to policy: Password must have uppercase characters': 'Password does not conform to policy: Password must have uppercase characters',
        };
        const [_, matchedError] = Object.entries(errMsgMap).find(([errMsg, ourMsg]) => (e.message || '').includes(errMsg)) ?? [
            'none',
            errMsgMap.default,
        ];
        setError(matchedError);
    }
};
const PasswordReset = ({ isAuthenticated }) => {
    const navigate = useNavigate();
    const [username, setUsername] = useState(undefined);
    const [password, setPassword] = useState(undefined);
    const [showPassword, setShowPassword] = useState(false);
    const [email, setEmail] = useState(undefined);
    const [code, setCode] = useState(undefined);
    const [error, setError] = useState(undefined);
    const [message, setMessage] = useState(undefined);
    const [resetStage, setResetStage] = useState(ResetStages.ASK_USER_TO_REQUEST_CODE);
    if (isAuthenticated)
        navigate(`/${NavSections.LOGIN}`);
    const codeRequestForm = (_jsxs("form", { noValidate: true, children: [_jsx(TextField, { id: 'username',
                name: 'username',
                value: username,
                label: 'Username',
                variant: 'outlined',
                margin: 'dense',
                required: true,
                fullWidth: true,
                autoFocus: true,
                autoComplete: 'username',
                helperText: username === undefined || username.length === 0
                    ? 'Please enter your username'
                    : !isValidUsername(username)
                        ? `Usernames must be 4 or more characters long`
                        : undefined,
                onChange: (e) => setUsername(e.target.value) }, 'username'), error && (_jsx(Alert, { severity: "error", onClose: () => setError(undefined), sx: { marginBlockEnd: 1 }, children: error })), message && (_jsx(Alert, { severity: "info", onClose: () => setMessage(undefined), sx: { marginBlockEnd: 1 }, children: message })), _jsx(Button, { type: 'submit',
                variant: 'contained',
                fullWidth: true,
                disabled: !username || !isValidUsername(username) || error !== undefined,
                onClick: (e) => {
                    e.preventDefault();
                    if (username) {
                        requestConfirmationCode(username, setMessage, setError, setEmail, setResetStage);
                        setError(undefined);
                        setMessage(undefined);
                    }
                }, children: "Request Validation Code" })] }));
    const enterCodeForm = (_jsxs("form", { noValidate: true, autoComplete: "off", children: [_jsx(TextField, { id: 'code',
                name: 'code',
                type: 'text',
                label: 'Validation code',
                variant: 'outlined',
                margin: 'dense',
                required: true,
                fullWidth: true,
                autoFocus: true,
                autoComplete: 'one-time-code',
                defaultValue: '',
                helperText: code === undefined || code.length === 0 ? `Please enter the code sent to ${email ?? 'your email'}` : undefined,
                onChange: (e) => setCode(e.target.value) }, 'code'), _jsx(TextField, { id: 'password',
                name: 'password',
                label: 'New password',
                variant: 'outlined',
                margin: 'dense',
                required: true,
                fullWidth: true,
                autoFocus: true,
                autoComplete: 'new-password',
                type: showPassword ? 'text' : 'password',
                error: !!password && !isValidPassword(password),
                helperText: password === undefined || password.length === 0 ? 'Please enter your new password' : !isValidPassword(password) ? passwordReminder : undefined,
                onChange: (e) => setPassword(e.target.value),
                InputProps: {
                    endAdornment: (_jsx(InputAdornment, { position: "end", children: _jsx(IconButton, { "aria-label": "toggle password visibility", onClick: () => setShowPassword(!showPassword), children: showPassword ? _jsx(Visibility, {}) : _jsx(VisibilityOff, {}) }) })),
                } }, 'password'), error && (_jsx(Alert, { severity: "error", onClose: () => setError(undefined), sx: { marginBlockEnd: 1 }, children: error })), message && (_jsx(Alert, { severity: "info", onClose: () => setMessage(undefined), sx: { marginBlockEnd: 1 }, children: message })), _jsx(Button, { type: 'submit',
                variant: 'contained',
                fullWidth: true,
                disabled: !username || !isValidUsername(username) || !password || !isValidPassword(password),
                onClick: (e) => {
                    e.preventDefault();
                    if (username && code && password) {
                        submitNewPassword(username, code, password, setMessage, setError, setResetStage, navigate);
                        setError(undefined);
                        setMessage(undefined);
                    }
                }, children: "Submit" })] }));
    let form = _jsx(_Fragment, {});
    switch (resetStage) {
        case ResetStages.ASK_USER_TO_REQUEST_CODE:
            form = codeRequestForm;
            break;
        case ResetStages.ASK_USER_TO_ENTER_RECEIVED_CODE:
            form = enterCodeForm;
            break;
    }
    return (_jsx(Container, { component: "main", maxWidth: "xs", sx: { paddingBlock: 1 }, children: _jsxs(Stack, { gap: 1, children: [_jsxs(Stack, { direction: "row", justifyContent: "center", alignItems: "center", gap: 1, children: [_jsx(Avatar, { children: _jsx(LockOutlinedIcon, {}) }), _jsx(Typography, { variant: "h5", children: "Password Reset" })] }), form, _jsx(Link, { to: `/${NavSections.LOGIN}`, children: "Back to login" })] }) }));
};
export default PasswordReset;
